import { Store } from '@ngxs/store';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { isArray } from 'lodash-es';
import { IMenuFilter } from '@nimbus-library/features/menu';
import { NimbusPermissionSet } from '@nimbus-global/features/permissions/models/enums/nimbus-permission-set.enum';
import { SelectedTeamState } from '@nimbus-global/features/shared/store/selected-team-state/selected-team.state';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { IPermissionManagerService } from '@nimbus-library/features/user-settings/services/contracts/i-permission-manager.service';

export interface IRoleFilterParameters {
  permissionSet: NimbusPermissionSet | NimbusPermissionSet[];
  ignoreTeamSelection?: boolean;
  negate?: boolean;
}

export class RoleFilter implements IMenuFilter<IRoleFilterParameters> {
  public static for(parameter: IRoleFilterParameters) {
    return { type: RoleFilter, option: parameter };
  }

  public constructor(
    private readonly store: Store,
    private readonly authenticationService: IAuthenticationService,
    private readonly permissionManagerService: IPermissionManagerService,
  ) {}

  public validate$(parameter: IRoleFilterParameters): Observable<boolean> {
    return combineLatest([this.store.select(SelectedTeamState.team), this.permissionManagerService.getPermissionsModel$()]).pipe(
      map(([team, permissionModel]) => {
        if (!permissionModel) {
          return false;
        }

        if (!team || !team.groupId || team.groupId === '' || parameter.ignoreTeamSelection) {
          if (parameter.negate) {
            return isArray(parameter.permissionSet)
              ? !!permissionModel.permissionSets.filter((p) => !parameter.permissionSet.includes(p.permissionSet)).length
              : permissionModel.permissionSets.some((p) => p.permissionSet !== parameter.permissionSet);
          }

          return isArray(parameter.permissionSet)
            ? parameter.permissionSet.some((p) => permissionModel.hasPermissionSet(p))
            : permissionModel.hasPermissionSet(parameter.permissionSet);
        }

        if (parameter.negate) {
          const teamPermissionSets = permissionModel.teams[team.groupId];
          return isArray(parameter.permissionSet)
            ? !!teamPermissionSets.filter((p) => !parameter.permissionSet.includes(p.permissionSet)).length
            : teamPermissionSets.some((p) => p.permissionSet !== parameter.permissionSet);
        }

        return permissionModel
          ? isArray(parameter.permissionSet)
            ? parameter.permissionSet.some((p) => permissionModel.hasTeamPermissionSet(team.groupId, p))
            : permissionModel.hasTeamPermissionSet(team.groupId, parameter.permissionSet)
          : false;
      }),
    );
  }
}
